import { useState, useEffect, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ClipboardList, Loader2, Trash2, Image as ImageIcon, CheckSquare, Square,
  PackageCheck, Award, Inbox, Search, X, Printer,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { gradeLabel } from "@/lib/grade";
import QrPrintModal from "@/components/QrPrintModal";

const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function Staging() {
  const qc = useQueryClient();
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [committing, setCommitting] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [qrCards, setQrCards] = useState([]);
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/staging");
      setItems(data);
      setSelected(new Set()); // default: nothing selected
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = (() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    const toks = q.split(/\s+/);
    return items.filter((it) => {
      const hay = `${it.name} ${it.set_name} ${it.card_number} ${it.condition} ${it.tcg_printing || ""}`.toLowerCase();
      return toks.every((t) => hay.includes(t));
    });
  })();
  const allSelected = filtered.length > 0 && filtered.every((i) => selected.has(i.id));
  const toggle = (id) => setSelected((prev) => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const toggleAll = () => setSelected((prev) => {
    const n = new Set(prev);
    if (allSelected) filtered.forEach((i) => n.delete(i.id));
    else filtered.forEach((i) => n.add(i.id));
    return n;
  });

  const ids = () => Array.from(selected);

  const commit = async () => {
    if (selected.size === 0) { toast.error("Select at least one card"); return; }
    await runCommit(ids());
  };

  const commitAll = async () => {
    if (items.length === 0) return;
    await runCommit(items.map((i) => i.id));
  };

  const runCommit = async (commitIds) => {
    if (!commitIds.length) return;
    setCommitting(true);
    try {
      const { data } = await api.post("/staging/commit", { ids: commitIds });
      toast.success(`Added to inventory — ${data.added} new, ${data.merged} merged`);
      qc.invalidateQueries({ queryKey: ["cards"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      await load();
      if (data.cards?.length) { setQrCards(data.cards); setQrOpen(true); }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setCommitting(false); }
  };

  const removeSelected = async () => {
    if (selected.size === 0) { toast.error("Select cards to remove"); return; }
    if (!window.confirm(`Remove ${selected.size} card(s) from staging? (e.g. keeping for PSA grading)`)) return;
    setRemoving(true);
    try {
      const { data } = await api.post("/staging/remove", { ids: ids() });
      toast.success(`Removed ${data.removed} from staging`);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setRemoving(false); }
  };

  const selItems = items.filter((i) => selected.has(i.id));
  const selCost = selItems.reduce((s, i) => s + (Number(i.cost) || 0) * (Number(i.quantity) || 0), 0);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-100">Staging</h1>
          <p className="text-sm text-slate-400 mt-1">Review cards from your buy lots. Deselect any you're grading or holding, then add the rest to inventory.</p>
        </div>
        {items.length > 0 && (
          <div className="flex gap-3">
            <button onClick={removeSelected} disabled={removing || selected.size === 0} data-testid="staging-remove-button"
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm text-rose-400 hover:bg-rose-500/10 disabled:opacity-50" style={{ borderColor: "var(--pk-border)" }}>
              {removing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Remove ({selected.size})
            </button>
            <button onClick={commitAll} disabled={committing} data-testid="staging-print-all-button"
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-amber-500/40 bg-amber-500/10 text-amber-300 font-semibold text-sm hover:bg-amber-500/20 disabled:opacity-50">
              {committing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />} Add All &amp; Print Labels
            </button>
            <button onClick={commit} disabled={committing || selected.size === 0} data-testid="staging-commit-button"
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-amber-500 text-[#1a1205] font-semibold text-sm hover:bg-amber-400 disabled:opacity-50">
              {committing ? <Loader2 className="h-4 w-4 animate-spin" /> : <PackageCheck className="h-4 w-4" />} Add {selected.size} to Inventory
            </button>
          </div>
        )}
      </div>

      {!loading && items.length > 0 && (
        <div className="relative mb-4 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="staging-search-input"
            placeholder="Search staged cards by name, set or number…"
            className="w-full pl-10 pr-9 py-2.5 rounded-xl border bg-transparent text-sm text-slate-100 placeholder:text-slate-600 outline-none focus:ring-2 focus:ring-amber-500/50"
            style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }} />
          {query && (
            <button onClick={() => setQuery("")} data-testid="staging-search-clear"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-200"><X className="h-4 w-4" /></button>
          )}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-500"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed flex flex-col items-center justify-center py-20 text-center" style={{ borderColor: "var(--pk-border)" }}>
          <div className="holo-shine h-14 w-14 rounded-2xl flex items-center justify-center border mb-4" style={{ borderColor: "var(--pk-border)" }}><Inbox className="h-6 w-6 text-amber-400" /></div>
          <h3 className="font-heading text-lg font-bold text-slate-200">Staging is empty</h3>
          <p className="text-sm text-slate-500 mt-1 max-w-sm">Build a lot in the Buying tab and send it here to review before it hits your inventory.</p>
        </div>
      ) : (
        <div className="rounded-2xl border overflow-hidden" style={{ background: "var(--pk-surface)", borderColor: "var(--pk-border)" }}>
          <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: "var(--pk-border)" }}>
            <button onClick={toggleAll} data-testid="staging-select-all" className="flex items-center gap-2 text-sm text-slate-300 hover:text-slate-100">
              {allSelected ? <CheckSquare className="h-4 w-4 text-amber-400" /> : <Square className="h-4 w-4" />}
              {allSelected ? "Deselect all" : "Select all"}
            </button>
            <span className="text-xs text-slate-500">{selected.size} selected · showing {filtered.length} of {items.length} · cost {fmt(selCost)}</span>
          </div>
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm" data-testid="staging-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b" style={{ borderColor: "var(--pk-border)" }}>
                  <th className="px-4 py-3 w-10"></th>
                  <th className="px-4 py-3 font-semibold">Card</th>
                  <th className="px-3 py-3 font-semibold">Cond</th>
                  <th className="px-3 py-3 font-semibold">Qty</th>
                  <th className="px-3 py-3 font-semibold">Market</th>
                  <th className="px-3 py-3 font-semibold">Cost / ea</th>
                  <th className="px-3 py-3 font-semibold">% Paid</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((it) => {
                  const on = selected.has(it.id);
                  return (
                    <tr key={it.id} data-testid="staging-row" onClick={() => toggle(it.id)}
                      className={`border-b cursor-pointer transition-colors ${on ? "bg-amber-500/5" : "hover:bg-[var(--pk-hover)]"}`} style={{ borderColor: "var(--pk-border-subtle)" }}>
                      <td className="px-4 py-3" onClick={(e) => { e.stopPropagation(); toggle(it.id); }}>
                        <div data-testid="staging-row-checkbox">{on ? <CheckSquare className="h-4 w-4 text-amber-400" /> : <Square className="h-4 w-4 text-slate-600" />}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-8 rounded bg-[var(--pk-card)] overflow-hidden shrink-0 flex items-center justify-center border" style={{ borderColor: "var(--pk-border)" }}>
                            {it.image_url ? <img src={it.image_url} alt="" className="h-full w-full object-cover" /> : <ImageIcon className="h-3 w-3 text-slate-700" />}
                          </div>
                          <div className="min-w-0">
                            <div className="text-slate-100 font-medium truncate max-w-[200px]">{it.name}</div>
                            <div className="text-[11px] text-slate-500 truncate">{it.set_name} · #{it.card_number} {it.tcg_printing ? `· ${it.tcg_printing}` : ""}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-slate-300">{it.item_type === "slab" ? gradeLabel(it) : it.condition}</td>
                      <td className="px-3 py-3 font-mono-num text-slate-100">{it.quantity}</td>
                      <td className="px-3 py-3 font-mono-num text-slate-300">{fmt(it.tcg_market_price)}</td>
                      <td className="px-3 py-3 font-mono-num text-emerald-400">{fmt(it.cost)}</td>
                      <td className="px-3 py-3 font-mono-num text-amber-400">{it.buy_pct ? `${Number(it.buy_pct).toFixed(0)}%` : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* Mobile stacked cards */}
          <div className="md:hidden divide-y" style={{ borderColor: "var(--pk-border-subtle)" }}>
            {filtered.map((it) => {
              const on = selected.has(it.id);
              return (
                <div key={it.id} data-testid="staging-row" onClick={() => toggle(it.id)}
                  className={`p-4 cursor-pointer transition-colors ${on ? "bg-amber-500/5" : ""}`}>
                  <div className="flex gap-3">
                    <div data-testid="staging-row-checkbox" className="pt-0.5 shrink-0">{on ? <CheckSquare className="h-5 w-5 text-amber-400" /> : <Square className="h-5 w-5 text-slate-600" />}</div>
                    <div className="h-16 w-12 rounded bg-[var(--pk-card)] overflow-hidden shrink-0 flex items-center justify-center border" style={{ borderColor: "var(--pk-border)" }}>
                      {it.image_url ? <img src={it.image_url} alt="" className="h-full w-full object-cover" /> : <ImageIcon className="h-4 w-4 text-slate-700" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-slate-100 font-semibold truncate">{it.name}</div>
                      <div className="text-[11px] text-slate-500 truncate">{it.set_name} · #{it.card_number} {it.tcg_printing ? `· ${it.tcg_printing}` : ""}</div>
                      <div className="flex items-center gap-3 mt-2 text-xs">
                        <span className="text-slate-300">{it.item_type === "slab" ? gradeLabel(it) : it.condition}</span>
                        <span className="text-slate-500">Qty <span className="font-mono-num text-slate-100">{it.quantity}</span></span>
                        <span className="text-slate-500">Mkt <span className="font-mono-num text-slate-300">{fmt(it.tcg_market_price)}</span></span>
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs">
                        <span className="text-slate-500">Cost <span className="font-mono-num text-emerald-400">{fmt(it.cost)}</span></span>
                        <span className="text-slate-500">% <span className="font-mono-num text-amber-400">{it.buy_pct ? `${Number(it.buy_pct).toFixed(0)}%` : "—"}</span></span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {filtered.length === 0 && (
            <div className="py-12 text-center text-sm text-slate-500" data-testid="staging-no-matches">No staged cards match “{query}”.</div>
          )}
          <div className="px-4 py-3 border-t flex items-center gap-2 text-xs text-slate-500" style={{ borderColor: "var(--pk-border)" }}>
            <Award className="h-3.5 w-3.5 text-slate-500" /> Tip: leave a card unselected (or remove it) if you plan to send it to PSA or handle it separately — it won't be added to inventory.
          </div>
        </div>
      )}
      <QrPrintModal open={qrOpen} cards={qrCards} onClose={() => setQrOpen(false)} />
    </div>
  );
}
